/**
 * Centralized Time Utilities
 *
 * This module provides time calculation functions using dayjs.
 * All apps should use these utilities for consistent time handling.
 *
 * @module time
 */

// Import dayjs for internal use and consumer export
import dayjsLib from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat.js";
import duration from "dayjs/plugin/duration.js";
import timezone from "dayjs/plugin/timezone.js";
import utc from "dayjs/plugin/utc.js";

// Initialize plugins
dayjsLib.extend(duration);
dayjsLib.extend(utc);
dayjsLib.extend(timezone);
dayjsLib.extend(advancedFormat);

// Export configured dayjs instance for direct use
export const dayjs = dayjsLib;

/**
 * Converts a timestamp (milliseconds since epoch) to a time string in HH:MM:SS format.
 *
 * @param timestampMs - Timestamp in milliseconds
 * @param tz - Optional timezone (defaults to UTC)
 * @returns Time string in HH:MM:SS format
 */
export function timestampToTimeString(
  timestampMs: number,
  tz?: string
): string {
  const d = tz ? dayjsLib(timestampMs).tz(tz) : dayjsLib(timestampMs).utc();
  return d.format("HH:mm:ss");
}

/**
 * Calculates the duration between two timestamps in minutes.
 * Handles shifts that cross midnight by adding 24 hours.
 *
 * @param startMs - Start timestamp in milliseconds
 * @param endMs - End timestamp in milliseconds
 * @returns Duration in minutes (integer)
 */
export function calculateDurationMinutes(
  startMs: number,
  endMs: number
): number {
  let calculatedDiffMs = endMs - startMs;

  // Handle shifts that cross midnight (24 hours in ms)
  if (calculatedDiffMs < 0) {
    calculatedDiffMs += 24 * 60 * 60 * 1000;
  }

  return Math.floor(dayjsLib.duration(calculatedDiffMs).asMinutes());
}

/**
 * Calculates hours worked from time strings (e.g., "09:00" to "17:00").
 * Handles shifts that cross midnight.
 *
 * @param startTime - Start time string in HH:MM or HH:MM:SS format
 * @param endTime - End time string in HH:MM or HH:MM:SS format
 * @returns Hours worked (integer, truncated)
 */
export function calculateHoursWorked(
  startTime: string,
  endTime: string
): number {
  const start = dayjsLib(`1970-01-01T${startTime}`);
  const end = dayjsLib(`1970-01-01T${endTime}`);

  let hoursWorkedDiffMs = end.diff(start);

  // Handle shifts that cross midnight
  if (hoursWorkedDiffMs < 0) {
    hoursWorkedDiffMs += 24 * 60 * 60 * 1000;
  }

  return Math.floor(dayjsLib.duration(hoursWorkedDiffMs).asHours());
}

/**
 * Calculates a daily bucket identifier from a timestamp.
 * Useful for deduplication and grouping by day.
 *
 * @param timestampMs - Timestamp in milliseconds (defaults to now)
 * @returns Daily bucket number (days since epoch)
 */
export function getDailyBucket(timestampMs: number = Date.now()): number {
  return Math.floor(dayjsLib(timestampMs).startOf("day").unix() / 86_400);
}

/**
 * Converts minutes to a human-readable duration string.
 *
 * @param totalMinutes - Total minutes
 * @returns Formatted string like "2h 30m" or "45m"
 */
export function formatDuration(totalMinutes: number): string {
  const dur = dayjsLib.duration(totalMinutes, "minutes");
  const hours = dur.hours();
  const minutes = dur.minutes();

  if (hours === 0) {
    return `${minutes}m`;
  }
  if (minutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${minutes}m`;
}

/**
 * Formats a date for display.
 *
 * @param date - Date, timestamp, or dayjs object
 * @param format - Format string (dayjs format)
 * @returns Formatted date string
 */
export function formatDate(
  date: Date | number | string,
  format: string
): string {
  return dayjsLib(date).format(format);
}

/**
 * Formats a date for display in UTC timezone.
 * Use when the underlying value represents a UTC date/time.
 *
 * @param date - Date, timestamp, or dayjs object
 * @param format - Format string (dayjs format)
 * @returns Formatted date string in UTC
 */
export function formatDateUTC(
  date: Date | number | string,
  format: string
): string {
  return dayjsLib(date).utc().format(format);
}

/**
 * Gets relative time from now (e.g., "5 minutes ago").
 * Note: Requires relativeTime plugin if used.
 *
 * @param date - Date, timestamp, or dayjs object
 * @returns Relative time string
 */
export function formatDistanceToNow(date: Date | number | string): string {
  const now = dayjsLib();
  const target = dayjsLib(date);
  const diffMs = now.diff(target);
  const dur = dayjsLib.duration(diffMs);

  const minutes = Math.floor(dur.asMinutes());
  const hours = Math.floor(dur.asHours());
  const days = Math.floor(dur.asDays());

  if (minutes < 1) {
    return "just now";
  }
  if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  }
  if (hours < 24) {
    return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  }
  if (days < 30) {
    return `${days} day${days === 1 ? "" : "s"} ago`;
  }

  return target.format("MMM D, YYYY");
}

/**
 * Parses a time string to total milliseconds from start of day.
 *
 * @param timeString - Time string in HH:MM or HH:MM:SS format
 * @returns Milliseconds from start of day
 */
export function parseTimeToMs(timeString: string): number {
  const d = dayjsLib(`1970-01-01T${timeString}`);
  return d.diff(dayjsLib("1970-01-01T00:00:00"));
}

/**
 * Gets the start of day timestamp for a given date.
 *
 * @param date - Date, timestamp, or dayjs object (defaults to now)
 * @param tz - Optional timezone
 * @returns Timestamp of start of day in milliseconds
 */
export function getStartOfDay(
  date: Date | number | string = Date.now(),
  tz?: string
): number {
  const d = tz ? dayjsLib(date).tz(tz) : dayjsLib(date);
  return d.startOf("day").valueOf();
}

/**
 * Gets the end of day timestamp for a given date.
 *
 * @param date - Date, timestamp, or dayjs object (defaults to now)
 * @param tz - Optional timezone
 * @returns Timestamp of end of day in milliseconds
 */
export function getEndOfDay(
  date: Date | number | string = Date.now(),
  tz?: string
): number {
  const d = tz ? dayjsLib(date).tz(tz) : dayjsLib(date);
  return d.endOf("day").valueOf();
}

// ============================================================================
// Time Unit Conversion Utilities
// ============================================================================
// These use bitwise OR for truncation to avoid Math.floor/Math.round lint warnings.
// The `| 0` operation truncates to 32-bit integer (equivalent to Math.trunc for positive numbers).

/**
 * Converts milliseconds to seconds (rounded to nearest integer).
 * Use for logging durations where whole seconds are sufficient.
 *
 * @param ms - Duration in milliseconds
 * @returns Duration in seconds (rounded)
 */
export function msToSeconds(ms: number): number {
  return Math.round(ms / 1000);
}

/**
 * Converts milliseconds to minutes (truncated).
 * Use for display of elapsed time in whole minutes.
 *
 * @param ms - Duration in milliseconds
 * @returns Duration in minutes (truncated)
 */
export function msToMinutes(ms: number): number {
  return Math.floor(ms / 60_000);
}

/**
 * Converts seconds to minutes (truncated).
 * Use for converting threshold values stored in seconds to minutes for display.
 *
 * @param seconds - Duration in seconds
 * @returns Duration in minutes (truncated)
 */
export function secondsToMinutes(seconds: number): number {
  return Math.floor(seconds / 60);
}

/**
 * Calculates elapsed time in seconds since a timestamp.
 * Useful for timers and durations.
 *
 * @param since - Start timestamp in milliseconds
 * @returns Elapsed time in seconds (truncated)
 */
export function elapsedSeconds(since: number): number {
  return Math.floor((Date.now() - since) / 1000);
}

/**
 * Calculates elapsed time in minutes since a timestamp.
 * Useful for wait times and hold durations.
 *
 * @param since - Start timestamp in milliseconds
 * @returns Elapsed time in minutes (truncated)
 */
export function elapsedMinutes(since: number): number {
  return Math.floor((Date.now() - since) / 60_000);
}

/**
 * Gets a random integer index for an array.
 * Use instead of Math.floor(Math.random() * length).
 *
 * @param length - Array length
 * @returns Random integer from 0 to length-1
 */
export function randomIndex(length: number): number {
  return Math.floor(Math.random() * length);
}
